import { type ReactNode, useEffect, useMemo, useState } from 'react';

import {
  type DropdownOption,
  ActionBar,
  ChakraBox,
  ChakraFlex,
  Divider,
  FAIconName,
  FireflyButton,
  InputGroupV2,
  ModalV2,
  ParticipantCard,
  SkeletonLoader,
  SwitchV2,
  Text,
  UniversalSelect,
} from '@equip.health/ui';
import './scheduler2.css';

import util, {
  getRecurrenceOptions,
  isMonthlyAllowedAppointmentTypes,
} from '~/components/common/calendar/util';
import { SCHEDULE_APPOINTMENT_OPTION } from '~/lib/constants/appointment.constants';
import {
  CALENDAR_NOT_INTEGRATED_ERROR,
  CalendarResource,
  CalendarResourceMapper,
  MINIMUM_PROVIDER_ATTENDEES_WARNING,
  NO_PATIENT_OR_SUPPORT_ATTENDEE_WARNING,
  SchedulerStep,
} from '~/lib/constants/calendar.constants';
import { ParticipantTypes } from '~/lib/constants/schedule.constants';
import {
  AppointmentAttendeesAllowed,
  SCHEDULE_CONFLICT_TEXT,
} from '~/lib/constants/scheduling.constants';
import type { Recurrence } from '~/lib/util/calendar.util';
import { noEligibleHostErrorMessage } from '~/lib/util/schedule/schedule.util';

import { useHistory } from 'react-router-dom';
import { PatientScheduleAppointmentEvents } from '~/analytics/patientEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { Path } from '~/routes/constants';
import ChangeHostConfirmationModal from './Modals/ChangeHostConfirmationModal';
import TimezoneMenuV2 from './TimezoneMenuV2';
import ProviderUniversalSelect from './ProviderUniversalSelect';
import { useDatadog } from '~/lib/hooks/useDatadog';

import { getLatestOutOfOfficeEndDate } from '~/lib/util/profile.util';

const TOP_LEVEL_ID = 'create-schedule-modal-v2';
interface ParticipantOptions {
  supportParticipants?: Participant[];
  providerParticipants?: Participant[];
  patientParticipant: Participant;
  hostParticipant: Participant;
}

interface SchedulerV2Props {
  appointmentSummary: () => ReactNode;
  conflicts: string[];
  currentDate: string;
  disableContinueButton: boolean;
  disableScheduleButton: boolean;
  defaultAppointmentType: string;
  showMissingAttendeesWarning: boolean;
  handleNext: () => void;
  handlePrevious: () => void;
  handleProviderSelect: (
    selectedProviderDetails: ProviderUniversalSelectOption,
  ) => void;
  handleRecurrenceChange: (value: Recurrence) => void;
  handleUpdateParticipant: (
    id: string,
    isChecked: boolean,
    participantType: ParticipantType,
    altHostParticipant?: Participant,
  ) => void;
  handleSave: () => void;
  isAppointmentTypesLoading: boolean;
  isCalendarConnected: boolean;
  isLoadingConflicts: boolean;
  isIgnoreAvailabilityChecked: boolean;
  isIgnoreAvailabilityDisabled: boolean;
  isOpen: boolean;
  onClose: (date: string) => void;
  onIgnoreAvailabilityChange: () => void;
  onSelectAppointmentType: (value: string) => void;
  onTimeZoneChange: (timeZoneId: string) => void;
  participantOptions: ParticipantOptions; // Options for rendering participants list
  sortedAppointmentTypes: AppointmentType[];
  step: SchedulerStep;
  showDatePicker: boolean;
}

export const appointmentInputWidth = 'calc((100% - 72px) / 3)';

export const enum ConflictErrors {
  OVERLAPPED_DATES_ERROR = 'dates-over-lapped',
  NO_ELIGIBLE_HOST = 'no-eligible-host',
  MISSING_ATTENDEES = 'missing-attendees',
}

let initialIsCalendarConnected = null;
let initialShowMissingAttendeeWarning = null;

export const participantCardPadding = '4px';

const SchedulerV2 = ({
  appointmentSummary,
  conflicts,
  currentDate,
  disableContinueButton,
  disableScheduleButton,
  defaultAppointmentType,
  handleProviderSelect,
  handleUpdateParticipant,
  isAppointmentTypesLoading,
  isCalendarConnected,
  isLoadingConflicts,
  isIgnoreAvailabilityChecked,
  isIgnoreAvailabilityDisabled,
  isOpen,
  handleNext,
  handlePrevious,
  handleRecurrenceChange,
  handleSave,
  onClose,
  onIgnoreAvailabilityChange,
  onSelectAppointmentType,
  onTimeZoneChange,
  participantOptions,
  showMissingAttendeesWarning,
  sortedAppointmentTypes,
  step,
  showDatePicker,
}: SchedulerV2Props) => {
  const history = useHistory();
  const { trackEvent } = useAnalytics();

  const { logError } = useDatadog();

  const [selectedAppointmentType, setSelectedAppointmentType] =
    useState<DropdownOption>(null);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [recurrenceOption, setRecurrenceOption] =
    useState<DropdownOption>(null);

  const [selectedReplacementHost, setSelectedReplacementHost] =
    useState<string>('');

  const [selectSlotMainSectionHeight, setSelectSlotMainSectionHeight] =
    useState<string>();

  useEffect(() => {
    const getHeight = () => {
      return `${
        (document.getElementById(`chakra-modal-${TOP_LEVEL_ID}-modal`)
          ?.clientHeight ?? 285) - 285
      }px`;
    };
    setSelectSlotMainSectionHeight(getHeight());
  }, [selectedAppointmentType, step, participantOptions]);

  const getAppointmentTypeOptions = () => {
    return (
      sortedAppointmentTypes?.map((type) => ({
        label: `${type?.appointmentType}, ${type?.durationInMinutes} minutes`,
        value: type?.appointmentTypeExtId,
      })) ?? []
    );
  };

  const participantUserIds = [
    participantOptions?.hostParticipant?.id,
    ...(participantOptions?.providerParticipants ?? []).map((each) => each?.id),
  ];

  if (initialIsCalendarConnected !== isCalendarConnected) {
    initialIsCalendarConnected = isCalendarConnected;
  }

  if (initialShowMissingAttendeeWarning !== showMissingAttendeesWarning) {
    initialShowMissingAttendeeWarning = showMissingAttendeesWarning;
  }

  const handleAppointmentTypeChange = (selected: UniversalSelectOption) => {
    setSelectedAppointmentType(selected);
    onSelectAppointmentType(selected.value);
    trackEvent(PatientScheduleAppointmentEvents.appointmentTypeSelected, {
      Source: AnalyticsSourceValues.SCHEDULE_APPOINTMENT_MODAL,
    });
  };

  const firstRecurrenceOption = useMemo(
    () =>
      getRecurrenceOptions({
        excludeMonthly: !selectedAppointmentType
          ? true
          : !isMonthlyAllowedAppointmentTypes(selectedAppointmentType),
      })[0],
    [selectedAppointmentType],
  );

  useEffect(() => {
    setRecurrenceOption(firstRecurrenceOption);
  }, [firstRecurrenceOption]);

  if (defaultAppointmentType && !selectedAppointmentType) {
    setSelectedAppointmentType(
      getAppointmentTypeOptions().find(
        (each) => each.value === defaultAppointmentType,
      ),
    );
  }

  const handleMakeHost = (participant: Participant) => {
    setSelectedReplacementHost(participant.id);
    setIsConfirmationModalOpen(true);
    trackEvent(
      PatientScheduleAppointmentEvents.makeHostKebabMenuButtonClicked,
      {
        Source: AnalyticsSourceValues.SCHEDULE_APPOINTMENT_MODAL,
      },
    );
  };

  const getKebabOptions = (participant: Participant) => {
    if (participant?.isEligibleToHost) {
      return [
        {
          id: `make-${participant.id}-host`,
          label: 'Make host',
          leftIcon: FAIconName.userCheckRegular,
          leftIconColor: 'neutral.primary',
          onClick: () => {
            handleMakeHost(participant);
          },
        },
      ];
    }
    return null;
  };

  const handleHostChangeConfirmation = () => {
    const updatedParticipantOptions = {
      ...participantOptions,
      hostParticipant: participantOptions.providerParticipants?.find(
        (p) => p.id === selectedReplacementHost,
      ),
    };
    handleUpdateParticipant(
      updatedParticipantOptions.hostParticipant.id,
      true,
      ParticipantTypes.HOST,
      updatedParticipantOptions.hostParticipant,
    );
    trackEvent(
      PatientScheduleAppointmentEvents.changeHostConfirmationModalChangeHostButtonClicked,
      {
        Source: AnalyticsSourceValues.SCHEDULE_APPOINTMENT_MODAL,
      },
    );
    setIsConfirmationModalOpen(false);
  };

  const onParticipantCardClick = ({
    isChecked,
    participant,
    participantType,
  }: {
    isChecked: boolean;
    participant: Participant;
    participantType: ParticipantType;
  }) => {
    handleUpdateParticipant(participant.id, isChecked, participantType);
  };

  const renderWarningActionBar = ({
    conflict,
    id,
    buttonMessage,
    buttonIcon,
    onButtonClick,
  }: {
    conflict: string | ReactNode;
    id: string;
    buttonMessage?: string;
    buttonIcon?: FAIconName;
    onButtonClick?: () => void;
  }) => {
    return (
      <ChakraBox marginBottom="8px">
        <ActionBar
          barIcon={FAIconName.exclamationTriangleRegular}
          buttonIcon={buttonIcon}
          buttonText={buttonMessage}
          id={`${TOP_LEVEL_ID}__${id}`}
          onButtonClick={onButtonClick}
          size="small"
          text={conflict}
          variant="warning"
        />
      </ChakraBox>
    );
  };

  const getNoEligibleHostError = (): ReactNode => {
    if (!participantOptions.hostParticipant && selectedAppointmentType) {
      const providerType = sortedAppointmentTypes?.find(
        (type: AppointmentType) =>
          type.appointmentTypeExtId === selectedAppointmentType.value,
      );

      const errorMessage = util.joinStringsWithOr(
        providerType?.providerTypeNames,
      );

      logError(new Error(errorMessage));

      return renderWarningActionBar({
        conflict: noEligibleHostErrorMessage(errorMessage),
        id: ConflictErrors.NO_ELIGIBLE_HOST,
      });
    }
    return null;
  };

  const getMissingAttendeesWarning = (): ReactNode => {
    if (showMissingAttendeesWarning) {
      const isProvidersOnly =
        sortedAppointmentTypes.find(
          (type: AppointmentType) =>
            type.appointmentTypeExtId === selectedAppointmentType?.value,
        )?.attendeesAllowed === AppointmentAttendeesAllowed.PROVIDER_ONLY;

      return renderWarningActionBar({
        conflict: isProvidersOnly
          ? MINIMUM_PROVIDER_ATTENDEES_WARNING
          : NO_PATIENT_OR_SUPPORT_ATTENDEE_WARNING,
        id: ConflictErrors.MISSING_ATTENDEES,
      });
    }
    return null;
  };

  const renderFooter = () => {
    if (step === SchedulerStep.SELECT_SLOT) {
      return (
        <ChakraFlex
          alignItems="center"
          justifyContent="space-between"
          marginTop="24px"
          width="100%"
        >
          <ChakraBox gap="10px" maxWidth="612px">
            {!isCalendarConnected &&
              renderWarningActionBar({
                conflict: CALENDAR_NOT_INTEGRATED_ERROR,
                id: `${TOP_LEVEL_ID}__link-calendar-button`,
                buttonMessage: 'Link Account',
                buttonIcon: FAIconName.linkRegular,
                onButtonClick: () => history.push(Path.CALENDAR_INTEGRATION),
              })}
            {getMissingAttendeesWarning()}
            {getNoEligibleHostError()}
          </ChakraBox>
          <ChakraFlex height="40px" width="288px">
            <FireflyButton
              id="continue-button"
              isDisabled={disableContinueButton}
              isFullWidth
              onClick={handleNext}
              rightIcon={FAIconName.arrowRightRegular}
            >
              Continue
            </FireflyButton>
          </ChakraFlex>
        </ChakraFlex>
      );
    }
    return (
      <ChakraFlex justifyContent="space-between" marginTop="6px" width="100%">
        <FireflyButton
          id="edit-appointment-button"
          leftIcon={FAIconName.arrowLeftRegular}
          onClick={handlePrevious}
          variant="secondary"
        >
          Back
        </FireflyButton>
        <FireflyButton
          id="schedule-appointment-button"
          isDisabled={disableScheduleButton || isLoadingConflicts}
          leftIcon={FAIconName.checkRegular}
          onClick={handleSave}
        >
          Schedule appointment
        </FireflyButton>
      </ChakraFlex>
    );
  };

  const recurrenceOptions = (selectedAppointmentType: DropdownOption) => {
    return getRecurrenceOptions({
      excludeMonthly: !isMonthlyAllowedAppointmentTypes(
        selectedAppointmentType,
      ),
    });
  };
  const hostOutOfOfficeEndDate = getLatestOutOfOfficeEndDate(
    participantOptions?.hostParticipant?.outOfOfficeItems,
  );

  return (
    <>
      <ModalV2
        footerContent={renderFooter()}
        id={TOP_LEVEL_ID}
        isOpen={isOpen}
        onClose={() => onClose(currentDate)}
        size="5xl"
        title={SCHEDULE_APPOINTMENT_OPTION}
      >
        <ChakraBox
          display={step === SchedulerStep.SELECT_SLOT ? 'block' : 'none'}
          height="55vh"
        >
          <ChakraFlex
            alignItems="center"
            gap="36px"
            height="71px"
            marginBottom="24px"
            width="100%"
          >
            <ChakraBox width={appointmentInputWidth}>
              <SkeletonLoader
                id={`${TOP_LEVEL_ID}__Skeleton-loader`}
                isLoaded={!isAppointmentTypesLoading}
              >
                <InputGroupV2
                  id={`${TOP_LEVEL_ID}__input-appointment-type`}
                  label="Appointment type"
                >
                  <UniversalSelect
                    id={`${TOP_LEVEL_ID}__appointment-type-options`}
                    isClearable={false}
                    isSingleSelect
                    modalId={TOP_LEVEL_ID}
                    onChange={handleAppointmentTypeChange}
                    options={getAppointmentTypeOptions()}
                    placeholder="Select an appointment type"
                    value={selectedAppointmentType}
                  />
                </InputGroupV2>
              </SkeletonLoader>
            </ChakraBox>
            {selectedAppointmentType && (
              <>
                <ChakraBox width={appointmentInputWidth}>
                  <InputGroupV2
                    id={`${TOP_LEVEL_ID}__input-recurrence-type`}
                    label="Recurrence"
                  >
                    <UniversalSelect
                      id={`${TOP_LEVEL_ID}__recurrence-type-options-2`}
                      isClearable={false}
                      isSingleSelect
                      modalId={TOP_LEVEL_ID}
                      onChange={(selected: DropdownOption) => {
                        handleRecurrenceChange(selected.value as Recurrence);
                        setRecurrenceOption(selected ?? firstRecurrenceOption);
                      }}
                      options={recurrenceOptions(selectedAppointmentType)}
                      placeholder="select frequency"
                      value={recurrenceOption}
                    />
                  </InputGroupV2>
                </ChakraBox>
                <ChakraBox width={appointmentInputWidth}>
                  <ChakraFlex alignItems="center" gap="10px">
                    <SwitchV2
                      id={`${TOP_LEVEL_ID}__scheduler__ignore-availability-switch`}
                      isChecked={isIgnoreAvailabilityChecked}
                      isDisabled={
                        isIgnoreAvailabilityDisabled || !isCalendarConnected
                      }
                      onChange={onIgnoreAvailabilityChange}
                      size="lg"
                    />
                    <Text>Ignore availability settings</Text>
                  </ChakraFlex>
                </ChakraBox>
              </>
            )}
          </ChakraFlex>
          <Divider
            id={`${TOP_LEVEL_ID}__appointment-type-divider`}
            marginBottom="24px"
          />
          {selectedAppointmentType && (
            <>
              <ChakraFlex
                gap="36px"
                height={selectSlotMainSectionHeight}
                justifyContent="space-between"
                position="relative"
              >
                <ChakraFlex
                  direction="column"
                  height="100%"
                  width={appointmentInputWidth}
                >
                  <ChakraBox marginBottom="12px">
                    <Text
                      color="neutral.secondary"
                      marginBottom="6px"
                      variant="caption"
                    >
                      Participants
                    </Text>

                    <ProviderUniversalSelect
                      filteredOutProviderIds={participantUserIds}
                      handleSelect={handleProviderSelect}
                      id={`${TOP_LEVEL_ID}__select-provider`}
                      modalId={`${TOP_LEVEL_ID}__select-provider__universal-select-modal`}
                      selectedProvider={null}
                      shouldAddCalendarProfile
                      shouldClearValueOnSelect
                      shouldDisableProviderOptionsWithoutCalendarId
                    />
                  </ChakraBox>
                  <ChakraBox height="100%" overflow="auto">
                    {participantOptions?.hostParticipant && (
                      <ChakraBox padding={participantCardPadding}>
                        <ParticipantCard
                          caption={
                            participantOptions?.hostParticipant?.secondaryText
                          }
                          id={`${TOP_LEVEL_ID}__${participantOptions?.hostParticipant?.id}`}
                          isHost
                          participantName={
                            participantOptions?.hostParticipant?.primaryText
                          }
                          oooDate={hostOutOfOfficeEndDate}
                          participantType="provider"
                          src={
                            participantOptions?.hostParticipant?.avatar
                              ?.thumbnailUrl ??
                            participantOptions?.hostParticipant?.avatar?.url
                          }
                        />
                      </ChakraBox>
                    )}
                    {participantOptions?.patientParticipant && (
                      <ChakraBox padding={participantCardPadding}>
                        <ParticipantCard
                          caption={
                            participantOptions?.patientParticipant
                              ?.secondaryText
                          }
                          id={`${TOP_LEVEL_ID}__${participantOptions?.patientParticipant?.id}`}
                          isSelected={
                            participantOptions?.patientParticipant?.isChecked
                          }
                          onCardClick={() =>
                            onParticipantCardClick({
                              isChecked:
                                !participantOptions?.patientParticipant
                                  ?.isChecked,
                              participant:
                                participantOptions?.patientParticipant,
                              participantType: 'patient',
                            })
                          }
                          participantName={
                            participantOptions?.patientParticipant?.primaryText
                          }
                          participantType="patient"
                        />
                      </ChakraBox>
                    )}
                    {participantOptions?.supportParticipants?.map((support) => (
                      <ChakraBox
                        key={support?.id}
                        padding={participantCardPadding}
                      >
                        <ParticipantCard
                          caption={support?.secondaryText}
                          id={`${TOP_LEVEL_ID}__${support?.id}`}
                          isSelected={support?.isChecked}
                          onCardClick={() =>
                            onParticipantCardClick({
                              isChecked: !support?.isChecked,
                              participant: support,
                              participantType: 'support',
                            })
                          }
                          participantName={support?.primaryText}
                          participantType="support"
                        />
                      </ChakraBox>
                    ))}
                    {participantOptions?.providerParticipants?.map(
                      (provider) => {
                        const providerOutOfOfficeEndDate =
                          getLatestOutOfOfficeEndDate(
                            provider.outOfOfficeItems,
                          );

                        return (
                          <ChakraBox
                            key={provider?.id}
                            padding={participantCardPadding}
                          >
                            <ParticipantCard
                              caption={provider?.secondaryText}
                              id={`${TOP_LEVEL_ID}__${provider?.id}`}
                              isSelected={provider?.isChecked}
                              kebabItems={getKebabOptions(provider)}
                              onCardClick={() =>
                                onParticipantCardClick({
                                  isChecked: !provider?.isChecked,
                                  participant: provider,
                                  participantType: 'provider',
                                })
                              }
                              oooDate={providerOutOfOfficeEndDate}
                              participantName={provider?.primaryText}
                              participantType="provider"
                              src={
                                provider?.avatar?.thumbnailUrl ??
                                provider?.avatar?.url
                              }
                            />
                          </ChakraBox>
                        );
                      },
                    )}
                  </ChakraBox>
                </ChakraFlex>

                <ChakraBox
                  height="100%"
                  onClick={(e) => e.preventDefault()}
                  overflow="auto"
                  width="66%"
                >
                  <ChakraBox
                    position="absolute"
                    right="40px"
                    top="-6px"
                    width="250px"
                    zIndex="1"
                  >
                    {showDatePicker && (
                      <TimezoneMenuV2 onChange={onTimeZoneChange} />
                    )}
                  </ChakraBox>
                  <ChakraBox
                    id={
                      CalendarResourceMapper[
                        CalendarResource.DATE_TIME_PICKER_V2
                      ].options.target_id
                    }
                    style={{
                      display: showDatePicker ? 'block' : 'none',
                      height: '100%',
                    }}
                  />
                </ChakraBox>
              </ChakraFlex>
            </>
          )}
        </ChakraBox>
        {step === SchedulerStep.REVIEW && (
          <SkeletonLoader
            id={`${TOP_LEVEL_ID}__review-loader`}
            isLoaded={!isLoadingConflicts}
          >
            {appointmentSummary()}
            {conflicts.length > 0 && (
              <ChakraBox marginBottom="6px" marginTop="24px">
                {renderWarningActionBar({
                  conflict: (
                    <Text>
                      {SCHEDULE_CONFLICT_TEXT}{' '}
                      <span style={{ fontWeight: 500 }}>
                        {conflicts.join(', ')}
                      </span>
                    </Text>
                  ),
                  id: ConflictErrors.OVERLAPPED_DATES_ERROR,
                })}
              </ChakraBox>
            )}
          </SkeletonLoader>
        )}
      </ModalV2>
      <ChangeHostConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => {
          setIsConfirmationModalOpen(false);
        }}
        onConfirm={handleHostChangeConfirmation}
      />
    </>
  );
};

export default SchedulerV2;
